import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../shared/ui/Button';
import { Badge } from '../shared/ui/Badge';
import { Checkbox } from '../shared/ui/Checkbox';
import { Skeleton } from '../shared/ui/Skeleton';
import { toast } from '../shared/ui/toast';
import { useAuth } from '../auth/useAuth';
import { useMailboxes, useUpdateMailbox } from '../features/mailbox/api';
import { ForwardingPrimaryCard } from '../features/forwarding/components/ForwardingPrimaryCard';
import { usePrimaryEmail } from '../features/forwarding/api';

/**
 * Настройки переадресации:
 * - привязка основного (личного) адреса с OTP-подтверждением + отвязка (Forwarding BC);
 * - per-mailbox включение/выключение пересылки (PATCH /mailboxes/{id} forwardingEnabled).
 */
export function ForwardingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, login } = useAuth();
  const mailboxesQuery = useMailboxes();
  const primaryQuery = usePrimaryEmail(isAuthenticated);
  const update = useUpdateMailbox();

  if (!authLoading && !isAuthenticated) {
    return (
      <div className="mx-auto max-w-md rounded-2xl bg-surface p-8 text-center shadow-card">
        <p className="text-fg-muted">{t('auth.loginPrompt')}</p>
        <Button className="mt-4" onClick={() => { login(); }}>
          {t('auth.login')}
        </Button>
      </div>
    );
  }

  const all = mailboxesQuery.data?.items ?? [];
  const active = all.filter((m) => m.status === 'active');
  const allOn = active.length > 0 && active.every((m) => m.forwardingEnabled);
  const someOn = active.some((m) => m.forwardingEnabled);

  // Переадресация работает только с подтверждённым основным адресом. Пока его нет —
  // чекбоксы неактивны и запрос на бэкенд не уходит (§3).
  const canForward = primaryQuery.data?.status === 'verified';

  const toggle = (id: string, value: boolean) => {
    if (!canForward) return;
    update.mutate(
      { id, forwardingEnabled: value },
      {
        onSuccess: () =>
          toast.success(value ? t('mailbox.toasts.forwardingOn') : t('mailbox.toasts.forwardingOff')),
      },
    );
  };

  const toggleAll = (value: boolean) => {
    if (!canForward) return;
    active.forEach((m) => {
      if (m.forwardingEnabled !== value) toggle(m.id, value);
    });
  };

  return (
    <div className="space-y-5">
      <button
        type="button"
        onClick={() => void navigate({ to: '/' })}
        className="flex items-center gap-2 text-sm text-fg-muted hover:text-fg"
      >
        <ArrowLeft className="h-4 w-4" /> {t('app.back')}
      </button>

      <header>
        <h1 className="text-3xl font-bold text-fg">{t('forwarding.title')}</h1>
        <p className="mt-1 max-w-2xl text-sm text-fg-muted">{t('forwarding.subtitle')}</p>
      </header>

      <ForwardingPrimaryCard enabled={isAuthenticated} />

      <section className="rounded-2xl bg-surface p-5 shadow-card">
        <h2 className="font-semibold text-fg">{t('forwarding.manageTitle')}</h2>
        <p className="mt-1 text-sm text-fg-muted">{t('forwarding.manageHint')}</p>

        {mailboxesQuery.isLoading ? (
          <Skeleton className="mt-4 h-32 w-full" />
        ) : (
          <div className="mt-4 space-y-3">
            {!canForward && (
              <p className="rounded-lg bg-page px-3 py-2 text-xs text-fg-muted">
                {t('forwarding.manageDisabledHint')}
              </p>
            )}
            <label className="flex items-center gap-3 text-sm font-medium text-fg">
              <Checkbox
                checked={allOn ? true : someOn ? 'indeterminate' : false}
                onCheckedChange={toggleAll}
                disabled={!canForward}
                aria-label={t('common.selectAll')}
              />
              {t('common.selectAll')}
            </label>
            {active.map((m) => (
              <label key={m.id} className="flex items-start gap-3">
                <Checkbox
                  checked={m.forwardingEnabled}
                  onCheckedChange={(v) => { toggle(m.id, v); }}
                  disabled={!canForward}
                  aria-label={m.address.full}
                />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="break-all text-sm text-fg">{m.address.full}</span>
                    {m.forwardingEnabled && (
                      <Badge tone="forwarding" dot>
                        {t('status.forwarding')}
                      </Badge>
                    )}
                  </span>
                  {m.description && (
                    <span className="block truncate text-xs text-fg-muted" title={m.description}>
                      {m.description}
                    </span>
                  )}
                </span>
              </label>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
